import { BarChart } from '@/components/Chart/BarChart';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableNum,
    TableRow,
} from '@/components/Table/Table';
import {
    type ClientsParams,
    clientsExportUrl,
    type ClientsReport,
    type CohortFigures,
} from '@/features/reports/api';
import { exportFileName } from '@/features/reports/clients/labels';
import { CLIENTS_COPY } from '@/features/reports/copy';
import { formatPounds, formatPoundsCompact } from '@/features/reports/format';
import { Definition, KpiTile, ReportCard } from '@/features/reports/parts';
import { PERIOD_AXIS } from '@/features/reports/periods';
import { ExportButton } from '@/features/reports/transactions/ExportButton';
import { cn } from '@/util/utils';

const COPY = CLIENTS_COPY.mix;

/**
 * Clients › New vs returning (ALS-87): the prototype's Overview card for the period, its four
 * figures against the period before, and per bucket each cohort's margin. A client is new when her
 * first order of all time is in the period and returning when she had ordered before it, never
 * both. A refund comes off the cohort she is in when it settles, before VAT, so the two cohorts add
 * up to Transactions › Margin.
 */
export function ClientMixView({
    report,
    params,
}: {
    report: ClientsReport;
    params: ClientsParams;
}) {
    const { kpis, series } = report.mix;
    const share = kpis.newShare;

    return (
        <>
            <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
                <KpiTile label={COPY.kpis.newClients} metric={kpis.newClients} format={String} />
                <KpiTile
                    label={COPY.kpis.returningClients}
                    metric={kpis.returningClients}
                    format={String}
                />
                <KpiTile
                    label={COPY.kpis.newSales}
                    metric={kpis.newSales}
                    format={formatPoundsCompact}
                />
                <KpiTile
                    label={COPY.kpis.returningSales}
                    metric={kpis.returningSales}
                    format={formatPoundsCompact}
                />
            </div>

            <ReportCard
                title={COPY.card}
                subtitle={COPY.subtitle(report.current.label)}
                actions={<Definition label={COPY.card}>{COPY.definition}</Definition>}
            >
                {share === null ? (
                    <p className="text-muted-foreground py-2 text-sm">{COPY.empty}</p>
                ) : (
                    <div className="flex flex-col gap-3">
                        <div
                            role="img"
                            aria-label={COPY.split(share)}
                            className="bg-report-5/30 flex h-2.5 overflow-hidden rounded-full"
                        >
                            <i
                                className="bg-report-1 block h-full"
                                style={{ width: `${share}%` }}
                            />
                            <i
                                className="bg-report-2 block h-full"
                                style={{ width: `${100 - share}%` }}
                            />
                        </div>
                        <MixRow
                            dot="bg-report-1"
                            label={COPY.new}
                            clients={kpis.newClients.value ?? 0}
                            sales={kpis.newSales.value ?? 0}
                        />
                        <MixRow
                            dot="bg-report-2"
                            label={COPY.returning}
                            clients={kpis.returningClients.value ?? 0}
                            sales={kpis.returningSales.value ?? 0}
                        />
                        <p className="text-muted-foreground text-xs">{COPY.note(share)}</p>
                    </div>
                )}
            </ReportCard>

            <ReportCard
                title={COPY.chart.title}
                subtitle={COPY.chart.subtitle(PERIOD_AXIS[params.period])}
            >
                <BarChart
                    title={COPY.chart.title}
                    labels={series.map((b) => b.label)}
                    series={[
                        {
                            label: COPY.new,
                            values: series.map((b) => b.new.marginAfterRefunds),
                            colour: 0,
                        },
                        {
                            label: COPY.returning,
                            values: series.map((b) => b.returning.marginAfterRefunds),
                            colour: 1,
                        },
                    ]}
                    format={formatPoundsCompact}
                />
            </ReportCard>

            <ReportCard
                title={COPY.table.title}
                subtitle={COPY.table.subtitle}
                actions={
                    <ExportButton
                        url={clientsExportUrl('Mix', params)}
                        fileName={exportFileName(['mix', params.period])}
                    />
                }
            >
                {/* Periods against two cohorts' measures is a matrix, so a phone scrolls it sideways
                    in a labelled region, as Transactions' margin table does. */}
                <Table region={COPY.table.title}>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{COPY.table.period}</TableHead>
                            <TableHead>{COPY.table.cohort}</TableHead>
                            <TableHead className="text-right">{COPY.table.clients}</TableHead>
                            <TableHead className="text-right">{COPY.table.orders}</TableHead>
                            <TableHead className="text-right">{COPY.table.revenue}</TableHead>
                            <TableHead className="text-right">{COPY.table.before}</TableHead>
                            <TableHead className="text-right">{COPY.table.refunds}</TableHead>
                            <TableHead className="text-right">{COPY.table.after}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {series.flatMap((b) => [
                            <CohortRow
                                key={`${b.from}-new`}
                                period={b.label}
                                cohort={COPY.new}
                                figures={b.new}
                            />,
                            <CohortRow
                                key={`${b.from}-returning`}
                                period=""
                                cohort={COPY.returning}
                                figures={b.returning}
                            />,
                        ])}
                    </TableBody>
                </Table>
            </ReportCard>
        </>
    );
}

function MixRow({
    dot,
    label,
    clients,
    sales,
}: {
    dot: string;
    label: string;
    clients: number;
    sales: number;
}) {
    return (
        <div className="flex items-center gap-3 text-sm">
            <span className="inline-flex flex-1 items-center gap-2 font-medium">
                <i aria-hidden className={cn('inline-block size-2 rounded-full', dot)} />
                {label}
            </span>
            <span className="text-muted-foreground">{CLIENTS_COPY.clients(clients)}</span>
            <span className="w-20 text-right font-medium">{formatPoundsCompact(sales)}</span>
        </div>
    );
}

function CohortRow({
    period,
    cohort,
    figures,
}: {
    period: string;
    cohort: string;
    figures: CohortFigures;
}) {
    return (
        <TableRow>
            <TableCell className="font-medium whitespace-nowrap">{period}</TableCell>
            <TableCell className="text-muted-foreground whitespace-nowrap">{cohort}</TableCell>
            <TableNum>{figures.clients}</TableNum>
            <TableNum>{figures.orders}</TableNum>
            <TableNum>{formatPounds(figures.revenue)}</TableNum>
            <TableNum>{formatPounds(figures.marginBeforeRefunds)}</TableNum>
            <TableNum>
                {figures.refunds === 0 ? formatPounds(0) : `-${formatPounds(figures.refunds)}`}
            </TableNum>
            <TableNum
                className={cn('font-medium', figures.marginAfterRefunds < 0 && 'text-destructive')}
            >
                {formatPounds(figures.marginAfterRefunds)}
            </TableNum>
        </TableRow>
    );
}
